import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import { AirPath } from "@domain/project/floorplan/air-path";
import { Floorplan, ProductTypesInGroup } from "@domain/project/floorplan/floorplan";
import { FloorplanAlarmDevice } from "@domain/project/floorplan/floorplan-alarm-device";
import { FloorplanFlameDetector } from "@domain/project/floorplan/floorplan-flame-detector";
import { FloorplanFlameDetectorPlaceholder } from "@domain/project/floorplan/floorplan-flame-detector-placeholder";
import { FloorplanFreeText } from "@domain/project/floorplan/floorplan-free-text";
import { FloorplanGasWarningCenter } from "@domain/project/floorplan/floorplan-gas-warning-center";
import { FloorplanGasWarningCenterPlaceholder } from "@domain/project/floorplan/floorplan-gas-warning-center-placeholder";
import { FloorplanImage } from "@domain/project/floorplan/floorplan-image";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanPlasticSign } from "@domain/project/floorplan/floorplan-plastic-sign";
import { FloorplanProductItem } from "@domain/project/floorplan/floorplan-product-item";
import { FloorplanSignalElement } from "@domain/project/floorplan/floorplan-signal-element";
import { FloorplanSimplePlaceholder } from "@domain/project/floorplan/floorplan-simple-placeholder";
import { FloorplanState } from "@domain/project/floorplan/floorplan-state";
import { FloorplanText } from "@domain/project/floorplan/floorplan-text";
import { FloorplanTransmitter } from "@domain/project/floorplan/floorplan-transmitter";
import { FloorplanTransmitterPlaceholder } from "@domain/project/floorplan/floorplan-transmitter-placeholder";
import { MeasurementLine } from "@domain/project/floorplan/measurement-line";
import { FloorplanCentralStation } from "@domain/project/floorplan/monitoring/floorplan-central-station";
import { FloorplanDisplay } from "@domain/project/floorplan/monitoring/floorplan-display";
import { FloorplanKvm } from "@domain/project/floorplan/monitoring/floorplan-kvm";
import { FloorplanPatientMonitor } from "@domain/project/floorplan/monitoring/floorplan-patient-monitor";
import { FloorplanTelemetry } from "@domain/project/floorplan/monitoring/floorplan-telemetry";
import { FloorplanUtility } from "@domain/project/floorplan/monitoring/floorplan-utility";
import { ProductItemGroup } from "@domain/project/floorplan/product-item-group";
import { ReferenceMeasure } from "@domain/project/floorplan/reference-measure";
import { CableRoute } from "@domain/project/floorplan/wiring/cable-route";
import { Pipeline } from "@domain/project/floorplan/wiring/pipeline";
import { ProtectiveTube } from "@domain/project/floorplan/wiring/protective-tube";
import { Wire } from "@domain/project/floorplan/wiring/wire";
import { DangerArea } from "@domain/project/floorplan/zones/danger-area";
import { DustExZone } from "@domain/project/floorplan/zones/dust-ex-zone";
import { ExZone } from "@domain/project/floorplan/zones/ex-zone";
import { IsolationRoom } from "@domain/project/floorplan/zones/isolation-room";
import { ItRoom } from "@domain/project/floorplan/zones/it-room";
import { OperatingTheatre } from "@domain/project/floorplan/zones/operating-theatre";
import { ProcedureRoom } from "@domain/project/floorplan/zones/procedure-room";
import { StandardRoom } from "@domain/project/floorplan/zones/standard-room";
import { WildcardZone } from "@domain/project/floorplan/zones/wildcard-zone";
import { ItemSelectIntention } from "@project/floorplanner/konva/floorplan-workspace-konva.adapter";
import { ReferenceMeasureData } from "@project/floorplanner/konva/reference-measure-konva";
import { Point } from "@utils/point";
import { Observable } from "rxjs";

export abstract class FloorplanWorkspace {
  abstract get selectedItemChanged$(): Observable<{ item: FloorplanItem | null; selectIntention: ItemSelectIntention | null }>;

  abstract get selectedItemsChanged$(): Observable<FloorplanItem[]>;

  abstract init(floorplanState: Floorplan, container: HTMLDivElement): Observable<void>;

  abstract waitForProductImages$?: () => Observable<undefined>;

  abstract waitForFloorplanImages$?: () => Observable<undefined>;

  abstract getCenterPoint(): Point;

  abstract getPointerPosition(): Point;

  abstract getBorderDimensions(): { width: number; height: number };

  abstract getStageDimensions(): { width: number; height: number };

  abstract setScale(factor: number): void;

  abstract setStagePosition(point: Point): void;

  abstract setLockState(floorplanItem: FloorplanItem): void;

  abstract setItemTextVisibility(floorplanItem: { floorplanItem: FloorplanItem; visible: boolean }): void;

  abstract setVisibilityState(floorplanState: FloorplanState): void;

  abstract setGlobalItemTextVisibilityState(floorplanState: FloorplanState): void;

  abstract addExZone(exZone: ExZone, select: boolean): void;

  abstract addDustExZone(dustExZone: DustExZone, select: boolean): void;

  abstract addWildcardZone(zone: WildcardZone, select: boolean): void;

  abstract addDangerArea(dangerArea: DangerArea, select: boolean): void;

  abstract addImage(floorplanImage: FloorplanImage, select: boolean): Observable<void>;

  abstract addMeasurementLine(measurementLine: MeasurementLine, select: boolean): void;

  abstract addReferenceMeasure(referenceMeasure: ReferenceMeasure, select: boolean): void;

  abstract addText(text: FloorplanText, select: boolean): void;

  abstract addFreeText(freeText: FloorplanFreeText, select: boolean): void;

  abstract addAirPath(airPath: AirPath, select: boolean): void;

  abstract addPipeline(pipeline: Pipeline, select: boolean): void;

  abstract addCableRoute(cableRoute: CableRoute, select: boolean): void;

  abstract addProtectiveTube(protectiveTube: ProtectiveTube, select: boolean): void;

  abstract addWire(wire: Wire, select: boolean): void;

  abstract addPatientMonitor(patientMonitor: FloorplanPatientMonitor | FloorplanSimplePlaceholder, select: boolean): void;

  abstract addCentralStation(centralStation: FloorplanCentralStation | FloorplanSimplePlaceholder, select: boolean): void;

  abstract addTelemetry(telemetry: FloorplanTelemetry | FloorplanSimplePlaceholder, select: boolean): void;

  abstract addKvm(kvm: FloorplanKvm | FloorplanSimplePlaceholder, select: boolean): void;

  abstract syncKvmReceivers(kvm: FloorplanKvm): void;

  abstract addDisplay(display: FloorplanDisplay | FloorplanSimplePlaceholder, select: boolean): void;

  abstract addUtility(utility: FloorplanUtility | FloorplanSimplePlaceholder, select: boolean): void;

  abstract addIsolationRoom(isolationRoom: IsolationRoom, select: boolean): void;

  abstract addItRoom(itRoom: ItRoom, select: boolean): void;

  abstract addOperatingTheatre(operatingTheatre: OperatingTheatre, select: boolean): void;

  abstract addProcedureRoom(procedureRoom: ProcedureRoom, select: boolean): void;

  abstract addStandardRoom(standardRoom: StandardRoom, select: boolean): void;

  abstract addTransmitter(transmitter: FloorplanTransmitter | FloorplanTransmitterPlaceholder, select: boolean): void;

  abstract addFlameDetector(flameDetector: FloorplanFlameDetector | FloorplanFlameDetectorPlaceholder, select: boolean): void;

  abstract addAlarmDevice(alarmDevice: FloorplanAlarmDevice | FloorplanSimplePlaceholder, select: boolean): void;

  abstract addGasWarningCenter(
    gasWarningCenter: FloorplanGasWarningCenter | FloorplanGasWarningCenterPlaceholder,
    select: boolean,
  ): void;

  abstract addSignalElement(signalElement: FloorplanSignalElement | FloorplanSimplePlaceholder, select: boolean): void;

  abstract addPlasticSign(plasticSign: FloorplanPlasticSign | FloorplanSimplePlaceholder, select: boolean): void;

  abstract createGroup(group: ProductItemGroup): void;

  abstract changeGroup(group: {
    product: ProductTypesInGroup;
    targetGroup: ProductItemGroup;
    sourceGroup: ProductItemGroup;
  }): void;

  abstract deleteGroup(group: ProductItemGroup): void;

  abstract deselectGroup(group: ProductItemGroup): void;

  abstract removeFromGroup(group: { product: ProductTypesInGroup; sourceGroup: ProductItemGroup }): void;

  abstract selectGroup(group: ProductItemGroup): void;

  abstract attachRemoteSensor(transmitter: FloorplanTransmitter | FloorplanTransmitterPlaceholder, select: boolean): void;

  abstract detachRemoteSensor(transmitter: FloorplanTransmitter | FloorplanTransmitterPlaceholder): void;

  abstract deleteItem(floorplanItem: FloorplanItem): void;

  abstract deselect(): void;

  abstract getSelectedItem(): FloorplanItem | null;

  abstract getSelectedItems(): FloorplanItem[];

  abstract selectItem(floorplanItem: FloorplanItem, selectIntention?: ItemSelectIntention): void;

  abstract refreshMeasurementLine(floorplanItem: FloorplanItem): void;

  abstract refreshReferenceMeasure(referenceMeasure: ReferenceMeasure): void;

  abstract resetReferenceMeasure(referenceMeasure: { floorplanItem: ReferenceMeasure; currentData: ReferenceMeasureData }): void;

  abstract refreshItemText(floorplanItem: FloorplanItem): void;

  abstract refreshDisplayName(floorplanItem: FloorplanItem): void;

  abstract refreshWarningSignVisibility(
    floorplanItem:
      | FloorplanProductItem<ProductConfiguration>
      | FloorplanTransmitterPlaceholder
      | FloorplanFlameDetectorPlaceholder
      | FloorplanSimplePlaceholder
      | FloorplanGasWarningCenterPlaceholder,
    visible: boolean,
  ): void;

  abstract exportFloorplanToPngUrl(): string;

  abstract destroy(): void;

  abstract refreshFloorplanOpacity(value: number): void;

  abstract setDisplayMode(displayMode: string): void;

  abstract refreshZone(
    zone:
      ExZone | DustExZone | DangerArea | WildcardZone | IsolationRoom | ItRoom | OperatingTheatre | ProcedureRoom | StandardRoom,
  ): void;
  abstract refreshAirPath(airPath: AirPath): void;
  abstract refreshCableRoute(cableRoute: CableRoute): void;
  abstract refreshPipeline(pipeline: Pipeline): void;
  abstract refreshProtectiveTube(protectiveTube: ProtectiveTube): void;
  abstract refreshWire(wire: Wire): void;

  abstract autoScaleItems(): void;

  abstract updateAirPathsItemTextVisibility(visible: boolean): void;

  abstract updateAlarmDevicesItemTextVisibility(visible: boolean): void;

  abstract updateCableRoutesItemTextVisibility(visible: boolean): void;

  abstract updateDangerAreasItemTextVisibility(visible: boolean): void;

  abstract updateDustExZonesItemTextVisibility(visible: boolean): void;

  abstract updateExZonesItemTextVisibility(visible: boolean): void;

  abstract updateGasWarningCentersItemTextVisibility(visible: boolean): void;

  abstract updateImagesItemTextVisibility(visible: boolean): void;

  abstract updateMeasurementLinesItemTextVisibility(visible: boolean): void;

  abstract updateReferenceMeasureItemTextVisibility(visible: boolean): void;

  abstract updatePipelinesItemTextVisibility(visible: boolean): void;

  abstract updatePlasticSignsItemTextVisibility(visible: boolean): void;

  abstract updateProtectiveTubesItemTextVisibility(visible: boolean): void;

  abstract updateSignalElementsItemTextVisibility(visible: boolean): void;

  abstract updateTextsItemTextVisibility(visible: boolean): void;

  abstract updateFreeTextsItemTextVisibility(visible: boolean): void;

  abstract updateTransmittersItemTextVisibility(visible: boolean): void;

  abstract updateFlameDetectorsItemTextVisibility(visible: boolean): void;

  abstract updateWildcardZonesItemTextVisibility(visible: boolean): void;

  abstract updateWiresItemTextVisibility(visible: boolean): void;

  abstract updatePatientMonitorsItemTextVisibility(visible: boolean): void;

  abstract updateCentralStationsItemTextVisibility(visible: boolean): void;

  abstract updateTelemetriesItemTextVisibility(visible: boolean): void;

  abstract updateKvmsItemTextVisibility(visible: boolean): void;

  abstract updateDisplaysItemTextVisibility(visible: boolean): void;

  abstract updateIsolationRoomsItemTextVisibility(visible: boolean): void;

  abstract updateItRoomsItemTextVisibility(visible: boolean): void;

  abstract updateOperatingTheatresItemTextVisibility(visible: boolean): void;

  abstract updateProcedureRoomsItemTextVisibility(visible: boolean): void;

  abstract updateStandardRoomsItemTextVisibility(visible: boolean): void;

  abstract refreshFreeText(freeText: FloorplanFreeText): void;
}
